import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  ChevronDown, 
  Shield, 
  UserCog, 
  Stethoscope, 
  Receipt, 
  Eye, 
  ClipboardCheck,
  CheckCircle,
  XCircle,
  Edit,
  Send
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Role icons and colors
const ROLE_CONFIG = {
  admin: { icon: Shield, color: "bg-red-500", label: "Admin" },
  pa_coordinator: { icon: UserCog, color: "bg-emerald-500", label: "PA Coordinator" },
  physician: { icon: Stethoscope, color: "bg-blue-500", label: "Physician" },
  billing: { icon: Receipt, color: "bg-purple-500", label: "Billing" },
  reviewer: { icon: ClipboardCheck, color: "bg-amber-500", label: "Reviewer" },
  viewer: { icon: Eye, color: "bg-slate-500", label: "Viewer" },
  decision_maker: { icon: Shield, color: "bg-indigo-500", label: "Decision Maker" },
  staff: { icon: Users, color: "bg-cyan-500", label: "Staff" }
};

export default function UserSwitcher() {
  const { user, setUser, setOrganization } = useAuth();
  const [demoUsers, setDemoUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.organization_id?.startsWith("org_demo")) {
      fetchDemoUsers();
    }
  }, [user?.organization_id]);

  const fetchDemoUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/demo-users`, { withCredentials: true });
      setDemoUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching demo users:", error);
    }
  };

  const switchUser = async (targetUser) => {
    if (targetUser.user_id === user?.user_id) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/switch-user`,
        { user_id: targetUser.user_id },
        { withCredentials: true }
      );
      
      setUser(response.data);
      setOrganization(response.data.organization);
      toast.success(`Switched to ${response.data.name} (${ROLE_CONFIG[response.data.role]?.label || response.data.role})`);
      
      // Reload to refresh all data
      window.location.reload();
    } catch (error) {
      toast.error("Failed to switch user");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Only show for demo accounts
  if (!user?.organization_id?.startsWith("org_demo")) {
    return null;
  }

  const currentRoleConfig = ROLE_CONFIG[user?.role] || ROLE_CONFIG.viewer;
  const CurrentIcon = currentRoleConfig.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="border-slate-700 bg-slate-800/50 hover:bg-slate-700 gap-2"
          data-testid="user-switcher-trigger"
        >
          <div className={`w-2 h-2 rounded-full ${currentRoleConfig.color}`} />
          <span className="text-slate-300 text-xs hidden sm:inline">{user?.name?.split(' ')[0]}</span>
          <Badge variant="outline" className="text-[10px] px-1 py-0 border-slate-600">
            {currentRoleConfig.label}
          </Badge>
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-slate-900 border-slate-700"
        data-testid="user-switcher-menu"
      >
        <DropdownMenuLabel className="text-slate-300">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>Switch Demo User</span>
          </div>
          <p className="text-xs text-slate-500 font-normal mt-1">
            Test different roles and permissions
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-700" />
        
        {/* Permission Legend */}
        <div className="px-2 py-2 text-xs text-slate-500">
          <div className="flex items-center gap-4 mb-1">
            <span className="flex items-center gap-1"><Edit className="w-3 h-3" /> Create/Edit</span>
            <span className="flex items-center gap-1"><ClipboardCheck className="w-3 h-3" /> Review</span>
            <span className="flex items-center gap-1"><Send className="w-3 h-3" /> Submit</span>
          </div>
        </div>
        <DropdownMenuSeparator className="bg-slate-700" />
        
        {demoUsers.map((demoUser) => {
          const roleConfig = ROLE_CONFIG[demoUser.role] || ROLE_CONFIG.viewer;
          const RoleIcon = roleConfig.icon;
          const isCurrentUser = demoUser.user_id === user?.user_id;
          const perms = demoUser.permissions || {};
          
          return (
            <DropdownMenuItem
              key={demoUser.user_id}
              onClick={() => switchUser(demoUser)}
              disabled={loading || isCurrentUser}
              className={`cursor-pointer flex items-start gap-3 p-3 ${isCurrentUser ? 'bg-slate-800' : 'hover:bg-slate-800'}`}
              data-testid={`switch-user-${demoUser.role}`}
            >
              <div className={`w-8 h-8 rounded-full ${roleConfig.color} flex items-center justify-center flex-shrink-0`}>
                <RoleIcon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-200 truncate">{demoUser.name}</span>
                  {isCurrentUser && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Current</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">{demoUser.title || roleConfig.label}</p>
                
                {/* Permissions indicators */}
                <div className="flex items-center gap-2 mt-1">
                  <span className="flex items-center gap-0.5" title="Can Create/Edit">
                    {perms.can_create || perms.can_edit ? (
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <XCircle className="w-3 h-3 text-slate-600" />
                    )}
                    <Edit className="w-3 h-3 text-slate-500" />
                  </span>
                  <span className="flex items-center gap-0.5" title="Can Review">
                    {perms.can_review ? (
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <XCircle className="w-3 h-3 text-slate-600" />
                    )}
                    <ClipboardCheck className="w-3 h-3 text-slate-500" />
                  </span>
                  <span className="flex items-center gap-0.5" title="Can Submit to TPA/Payer">
                    {perms.can_submit ? (
                      <CheckCircle className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <XCircle className="w-3 h-3 text-slate-600" />
                    )}
                    <Send className="w-3 h-3 text-slate-500" />
                  </span>
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
        
        <DropdownMenuSeparator className="bg-slate-700" />
        <div className="px-3 py-2 text-[10px] text-slate-600">
          All demo accounts use password: <code className="text-slate-500">demo123</code>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
